import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  computeTotalizerRun,
  mergeFlowFamilyData,
  resolveFlowTemplate,
} from './flow-calibration-profile';
import {
  certificateImageAsDataUrl,
  drawCertificateFooter,
  drawCertificateHeader,
  drawCertificateLegalText,
  drawReferenceStandardsTable,
} from './certificate-pdf-layout';

const LOGO_URL = '/logo-calins-color-hd.png';
const SIGNATURE_URL = new URL('../assets/firma-daniel-kemeny.png', import.meta.url).href;

function numberOrNull(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function fmt(value, decimals = 3) {
  const number = numberOrNull(value);
  return number === null ? '—' : number.toFixed(decimals).replace('.', ',');
}

function fmtPct(value, decimals = 3) {
  const number = numberOrNull(value);
  return number === null ? '—' : `${number.toFixed(decimals).replace('.', ',')} %`;
}

function dmy(value) {
  if (!value) return '—';
  const text = String(value);
  const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(text) ? `${text}T12:00:00` : text);
  if (Number.isNaN(date.getTime())) return '—';
  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
}

function certificateNumber(...values) {
  for (const value of values) {
    const text = String(value ?? '').trim();
    if (/^\d+$/.test(text)) return text;
    const match = text.match(/OS-(\d+)-(\d+)$/i);
    if (match) return `${match[1]}${match[2]}`;
  }
  return '—';
}

async function imageAsDataUrl(url) {
  return certificateImageAsDataUrl(url);
}

function header(doc, { logo, number, page, pages, first = false }) {
  drawCertificateHeader(doc, { logo, number, page, pages, first });
}

function footer(doc, page, pages) {
  drawCertificateFooter(doc, page, pages);
}

function legalText(doc, y) {
  return drawCertificateLegalText(doc, y);
}

function labelValue(doc, label, value, x, y, valueX = x + 42) {
  doc.setFont('helvetica', 'bold');
  doc.text(label, x, y);
  doc.setFont('helvetica', 'normal');
  doc.text(String(value ?? '—'), valueX, y);
}

function drawPageOne(doc, { session, instrument }) {
  const template = resolveFlowTemplate(session.template_code);
  const determination = template.subfamily === 'mass'
    ? 'Caudal másico'
    : template.subfamily === 'electromagnetic'
      ? 'Caudal electromagnético'
      : template.subfamily === 'mechanical_totalizer'
        ? 'Mecánico / totalizador'
        : template.subfamily === 'ultrasonic'
          ? 'Caudal ultrasónico'
          : 'Flotámetro';
  let y = legalText(doc, 66) + 3;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('ESPECIFICACIONES', 14, y);
  y += 8;
  doc.setFontSize(9.5);
  const unit = session.instrument_unit || template.unit;
  const left = [
    ['Objeto:', session.instrument_object || instrument?.type || template.objectFallback],
    ['Fabricante:', session.instrument_brand || instrument?.brand || '—'],
    ['Modelo:', session.instrument_model || instrument?.model || '—'],
    ['N.° de serie:', session.instrument_serial || instrument?.serial || '—'],
    ['TAG:', session.instrument_tag || instrument?.tag || '—'],
    ['Intervalo:', `${fmt(session.instrument_range_min, 2)} a ${fmt(session.instrument_range_max, 2)} ${unit}`],
  ];
  const right = [
    ['Determinación:', determination],
    ['Resolución:', `${fmt(session.instrument_resolution, 4)} ${unit}`],
    ['Tolerancia:', session.instrument_class == null ? '—' : fmtPct(session.instrument_class)],
    ['Fecha de calibración:', dmy(session.calibration_date)],
    ['Fecha de emisión:', dmy(session.report_issue_date)],
    ['Válido hasta:', dmy(session.valid_until)],
  ];
  left.forEach(([label, value], index) => labelValue(doc, label, value, 14, y + (index * 5.5), 48));
  right.forEach(([label, value], index) => labelValue(doc, label, value, 110, y + (index * 5.5), 150));
  y += 43;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('CLIENTE', 14, y);
  y += 8;
  doc.setFontSize(9.5);
  [
    ['Razón social:', session.client_name],
    ['Dirección:', session.client_address],
    ['Ciudad:', session.client_city],
    ['Provincia:', session.client_province],
  ].forEach(([label, value], index) => labelValue(doc, label, value || '—', 26, y + (index * 5.5), 58));
  y += 31;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('CONDICIONES AMBIENTALES', 14, y);
  autoTable(doc, {
    startY: y + 7,
    margin: { left: 28, right: 28 },
    theme: 'grid',
    styles: { fontSize: 9, halign: 'center', cellPadding: 2, lineWidth: 0.1 },
    headStyles: { fillColor: [180, 215, 225], textColor: [0, 0, 0], fontStyle: 'bold' },
    head: [['Temperatura ambiente', 'Presión atmosférica', 'Humedad relativa']],
    body: [[
      `${fmt(session.ambient_temp_avg, 1)} °C`,
      `${fmt(session.ambient_pressure_avg, 1)} hPa`,
      `${fmt(session.ambient_humidity_avg, 1)} %HR`,
    ]],
  });
}

function groupRawPoints(points = []) {
  const rows = {};
  points.forEach((point) => {
    const pointNo = Number(point.point_no);
    const repeat = Number(point.series_no);
    const row = rows[pointNo] || { pointNo, setpoint: point.setpoint };
    row[`r${repeat}Pattern`] = point.pattern_reading;
    row[`r${repeat}Instrument`] = point.instrument_reading;
    rows[pointNo] = row;
  });
  return Object.values(rows).sort((a, b) => a.pointNo - b.pointNo);
}

function drawPageTwo(doc, { session, points, results }) {
  const template = resolveFlowTemplate(session.template_code);
  const familyData = mergeFlowFamilyData(session.family_data, template.code);
  const unit = session.instrument_unit || template.unit;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('RESULTADOS OBTENIDOS DURANTE LA CALIBRACIÓN', 14, 40);

  const rawRows = groupRawPoints(points);
  autoTable(doc, {
    startY: 47,
    margin: { left: 10, right: 10, top: 35 },
    theme: 'grid',
    styles: { fontSize: 7.4, halign: 'center', cellPadding: 1.4, lineWidth: 0.1 },
    headStyles: { fillColor: [180, 215, 225], textColor: [0, 0, 0], fontStyle: 'bold' },
    head: [[
      { content: 'Pto.', rowSpan: 2 },
      { content: `Nominal\n${unit}`, rowSpan: 2 },
      ...Array.from({ length: template.repeatCount }, (_, index) => ({ content: `Serie ${index + 1}`, colSpan: 2 })),
    ], Array.from({ length: template.repeatCount }, () => ['Patrón', 'Instrumento']).flat()],
    body: rawRows.map((row) => [row.pointNo, fmt(row.setpoint), ...Array.from(
      { length: template.repeatCount },
      (_, index) => [fmt(row[`r${index + 1}Pattern`]), fmt(row[`r${index + 1}Instrument`])],
    ).flat()]),
  });

  const sortedResults = [...results].sort((a, b) => a.point_no - b.point_no);
  autoTable(doc, {
    startY: (doc.lastAutoTable?.finalY || 100) + 10,
    margin: { left: 10, right: 10, top: 35 },
    theme: 'grid',
    styles: { fontSize: 7.4, halign: 'center', cellPadding: 1.6, lineWidth: 0.1 },
    headStyles: { fillColor: [180, 215, 225], textColor: [0, 0, 0], fontStyle: 'bold' },
    head: [['Pto.', 'Media patrón', 'Media instrumento', 'Error', 'Error %', 'Repetibilidad', 'U expandida', 'Conforme']],
    body: sortedResults.map((result) => [
      result.point_no,
      fmt(result.pattern_mean),
      fmt(result.instrument_mean),
      fmt(result.error_abs),
      fmtPct(result.error_pct),
      fmt(result.repeatability),
      fmt(result.u_reported),
      result.in_tolerance == null ? '—' : result.in_tolerance ? 'Sí' : 'No',
    ]),
  });

  const maxError = sortedResults.reduce((maximum, result) => Math.max(maximum, Math.abs(numberOrNull(result.error_abs) || 0)), 0);
  const maxUncertainty = sortedResults.reduce((maximum, result) => Math.max(maximum, Math.abs(numberOrNull(result.u_reported) || 0)), 0);
  const y = (doc.lastAutoTable?.finalY || 170) + 10;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text(`Error absoluto máximo: ${fmt(maxError)} ${unit}`, 18, y);
  doc.text(`Incertidumbre expandida máxima: ${fmt(maxUncertainty)} ${unit}`, 18, y + 6);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text('Error del instrumento = indicación del instrumento - valor del patrón.', 18, y + 14);

  if (template.measurementMode === 'mechanical_totalizer') {
    const totalizerRows = (familyData.totalizer_runs || []).map((run) => ({ run, metrics: computeTotalizerRun(run, familyData) })).filter((item) => item.metrics);
    if (totalizerRows.length) {
      autoTable(doc, {
        startY: y + 22,
        margin: { left: 10, right: 10, top: 35 },
        theme: 'grid',
        styles: { fontSize: 7.1, halign: 'center', cellPadding: 1.3, lineWidth: 0.1 },
        headStyles: { fillColor: [180, 215, 225], textColor: [0, 0, 0], fontStyle: 'bold' },
        head: [['Serie', 'Ensayo', `Inicial (${familyData.totalizer_volume_unit})`, `Final (${familyData.totalizer_volume_unit})`, 'Tiempo (s)', 'Delta', 'Caudal calculado (m³/h)']],
        body: totalizerRows.map(({ run, metrics }) => [run.series_no, run.run_no, fmt(run.initial_total), fmt(run.final_total), fmt(metrics.elapsed_seconds, 0), fmt(metrics.delta), fmt(metrics.calculated_flow_m3_h)]),
      });
    }
  }
}

function drawPageThree(doc, { session, familyData, signature }) {
  const template = resolveFlowTemplate(session.template_code);
  let y = 42;
  const reference = familyData.standard_configuration?.reference || {};
  const asset = reference.asset || {};
  const capability = reference.capability || {};
  const certificate = reference.certificate || {};
  const budget = familyData.standard_budget || {};

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('MÉTODO Y PROCEDIMIENTO', 14, y);
  y += 8;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.text(familyData.method, 14, y);
  y += 6;
  doc.text(familyData.procedure, 14, y);
  y += 11;

  const configuration = [
    ['Fluido', familyData.fluid || '—'],
    ['Posición / montaje', familyData.installation_position || '—'],
  ];
  if (template.functionKey === 'volumetric_flow' && template.subfamily !== 'rotameter') {
    configuration.push(
      ['Cañería', `Ø ${fmt(familyData.pipe_diameter_mm, 1)} mm · espesor ${fmt(familyData.pipe_thickness_mm, 1)} mm`],
      ['Material / recubrimiento', familyData.pipe_lining || '—'],
      ['Tramos rectos', `${fmt(familyData.upstream_straight_length_d, 1)} D aguas arriba · ${fmt(familyData.downstream_straight_length_d, 1)} D aguas abajo`],
    );
  }
  if (template.subfamily === 'electromagnetic') configuration.push(['Conductividad', `${fmt(familyData.conductivity_us_cm, 1)} µS/cm`]);
  if (template.subfamily === 'ultrasonic') configuration.push(
    ['Transductores', `${familyData.transducer_model || '—'} · ${familyData.transducer_mounting || '—'}`],
    ['Separación', `${fmt(familyData.transducer_spacing_mm, 1)} mm`],
  );
  if (template.subfamily === 'mechanical_totalizer') configuration.push(
    ['Totalizador', `Unidad ${familyData.totalizer_volume_unit || '—'} · factor de pulso ${fmt(familyData.pulse_factor)}`],
  );
  if (template.subfamily === 'rotameter') configuration.push(
    ['Servicio', familyData.rotameter_service === 'gas' ? 'Gas / caudal normalizado' : 'Líquido'],
    ['Condiciones de referencia', `${fmt(familyData.reference_temperature_c, 1)} °C · ${fmt(familyData.reference_pressure_hpa, 1)} hPa`],
    ['Densidad', `${fmt(familyData.gas_density_kg_m3, 4)} kg/m³`],
  );

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('CONFIGURACIÓN DEL ENSAYO', 14, y);
  autoTable(doc, {
    startY: y + 4,
    margin: { left: 14, right: 14 },
    theme: 'grid',
    styles: { fontSize: 7.5, cellPadding: 1.35, lineWidth: 0.1 },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 38 }, 2: { fontStyle: 'bold', cellWidth: 38 } },
    body: Array.from({ length: Math.ceil(configuration.length / 2) }, (_, index) => [
      ...(configuration[index * 2] || [' ', ' ']),
      ...(configuration[(index * 2) + 1] || [' ', ' ']),
    ]),
  });
  y = (doc.lastAutoTable?.finalY || y) + 8;

  drawReferenceStandardsTable(doc, {
    startY: y,
    references: [{ ...reference, usage: 'Referencia' }],
  });

  y = (doc.lastAutoTable?.finalY || y) + 11;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('PRESUPUESTO DE INCERTIDUMBRE', 14, y);
  autoTable(doc, {
    startY: y + 5,
    margin: { left: 14, right: 14 },
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 1.8, lineWidth: 0.1 },
    headStyles: { fillColor: [180, 215, 225], textColor: [0, 0, 0], fontStyle: 'bold' },
    head: [['Contribución', 'Tratamiento', 'Dato de entrada']],
    body: [
      ['Calibración del patrón', 'U/k', capability.metadata?.certified_uncertainty_percent == null ? `Según resultados · k=${fmt(certificate.coverage_factor, 0)}` : `${fmtPct(capability.metadata?.certified_uncertainty_percent)} · k=${fmt(certificate.coverage_factor, 0)}`],
      ['Resolución del instrumento', 'd/sqrt(12)', `${fmt(session.instrument_resolution, 4)} ${session.instrument_unit || template.unit}`],
      ['Repetibilidad', 's/sqrt(n)', `Calculada por punto con ${template.repeatCount} ${template.repeatCount === 1 ? 'lectura' : 'series'}`],
      ['Deriva del patrón', 'Límite/sqrt(3)', fmtPct(budget.drift_percent)],
      ['Influencia de temperatura', 'Límite/sqrt(3)', `${fmt(budget.temperature_coefficient_percent_per_10c)} % / 10 °C`],
      ['Otras contribuciones', 'Límite/sqrt(3)', `${fmt(budget.other_uncertainty)} ${session.instrument_unit || template.unit}`],
    ],
  });

  y = (doc.lastAutoTable?.finalY || y) + 12;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('OBSERVACIONES', 14, y);
  y += 8;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  const paragraphs = [
    'La incertidumbre expandida se calculó multiplicando la incertidumbre estándar combinada por k=2, correspondiente a un nivel de confianza cercano al 95 %.',
    'Las lecturas del patrón se corrigen cuando el certificado informa una corrección. La incertidumbre se toma del certificado fijado en la sesión.',
  ];
  if (session.notes) paragraphs.push(String(session.notes));
  paragraphs.forEach((paragraph) => {
    const lines = doc.splitTextToSize(paragraph, 180);
    doc.text(lines, 14, y);
    y += (lines.length * 4.5) + 4;
  });
  if (signature) doc.addImage(signature, 'PNG', 78, 238, 44, 29, undefined, 'FAST');
}

export async function generateFlowNoSacCertificatePdf(
  { session, instrument, points = [], results = [] },
  options = {},
) {
  if (!session) throw new Error('La sesión es requerida.');
  if (!results.length) throw new Error('No hay resultados para certificar.');

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const [logo, signature] = await Promise.all([
    imageAsDataUrl(options.logoUrl || LOGO_URL),
    imageAsDataUrl(options.signatureUrl || SIGNATURE_URL),
  ]);
  const number = certificateNumber(
    options.serviceOrderCode,
    session.service_order_code,
    session.certificate_number,
    instrument?.lab_id,
  );
  const familyData = mergeFlowFamilyData(session.family_data, session.template_code);

  drawPageOne(doc, { session, instrument });
  doc.addPage();
  drawPageTwo(doc, { session, points, results });
  doc.addPage();
  drawPageThree(doc, { session, familyData, signature });

  const pages = doc.getNumberOfPages();
  for (let page = 1; page <= pages; page += 1) {
    doc.setPage(page);
    header(doc, { logo, number, page, pages, first: page === 1 });
    footer(doc, page, pages);
  }

  if (options.returnBlob) {
    return new Blob([doc.output('arraybuffer')], { type: 'application/pdf' });
  }
  doc.save(options.filename || `${number !== '—' ? number : 'R-DT-PC-FLOW'}.pdf`);
  return null;
}

